import { type ReactNode, useEffect, useRef } from "react";
import { Button } from "./Button.tsx";

/**
 * A confirmation in the page, in place of `window.confirm` (audits/2026-10-29/gui/
 * principles.md §5) — the same question, laid out where the state beside it can be
 * shown, and not a browser box a page can be told to suppress.
 *
 * 🔴 It carries the caller's words and decides nothing. The question is the copy
 * constant the caller passes (`enablePrompt` for a start), as it stands; the two
 * labels are the caller's too. Escape and Cancel both answer `onCancel` — a
 * confirmation that can be left without the confirm press must be left as «no».
 */
export interface ModalProps {
	open: boolean;
	title: ReactNode;
	children: ReactNode;
	confirmLabel: string;
	cancelLabel: string;
	onConfirm: () => void;
	onCancel: () => void;
	/** Shown under the question, beside nothing it could be mistaken for. */
	aside?: ReactNode;
}

export function Modal({
	open,
	title,
	children,
	confirmLabel,
	cancelLabel,
	onConfirm,
	onCancel,
	aside,
}: ModalProps) {
	const ref = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const dialog = ref.current;
		if (!dialog) return;
		if (open && !dialog.open) dialog.showModal();
		if (!open && dialog.open) dialog.close();
	}, [open]);

	return (
		<dialog
			ref={ref}
			onCancel={(e) => {
				// Escape: the browser would close it on its own; close it through the
				// caller instead, so `open` and the dialog never disagree.
				e.preventDefault();
				onCancel();
			}}
			className="w-[min(40rem,92vw)] rounded-xl border border-line bg-surface p-5 text-content shadow-lg backdrop:bg-black/50"
		>
			<h2 className="text-base font-semibold text-content">{title}</h2>
			<div className="mt-3 rounded-md border border-line bg-canvas p-3 text-sm">{children}</div>
			{aside != null && <div className="mt-3 text-sm text-muted">{aside}</div>}
			<div className="mt-4 flex justify-end gap-2">
				<Button type="button" variant="outline" size="sm" onClick={onCancel}>
					{cancelLabel}
				</Button>
				<Button type="button" variant="primary" size="sm" onClick={onConfirm}>
					{confirmLabel}
				</Button>
			</div>
		</dialog>
	);
}
